import { Fragment, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Avatar, Chip } from "@mui/material";
import dayjs from "dayjs";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseDropdownV2 from "../../components/ui/BaseDropdownV2";
import BaseConfirmModal from "../../components/ui/BaseConfirmModal";
import SendEmailToEngineerModal from "./components/SendEmailToEngineerModal";
import AdminFilters from "./AdminFilters";
import { getAdminFreelanceCompanyMembers } from "../../services/api/freelanceCompany";
import {
  activateEngineer,
  approveEngineerProfile,
  deactivateEngineer,
} from "../../services/api/admin";
import { showToast } from "../../store/toast/toastSlice";
import { setAdminFilters } from "../../store/filters/adminFiltersSlice";
import { debounce, formatPhoneNumber } from "../../utils/helpers";
import { INCOMPLETE_CHIP_COLOR } from "../../utils/variables";
import BaseWrapper from "../../components/ui/BaseWrapper";
import IdentityFiles from "../../components/ui/IdentityFiles";

const menuOptions = [
  { label: "View Profile", value: "VIEW", icon: <i className="fa-solid fa-eye" /> },
  { label: "Approve", value: "APPROVE", icon: <i className="fa-solid fa-square-check" /> },
  { label: "Activate", value: "APPROVED", icon: <i className="fa-solid fa-toggle-on" /> },
  { label: "Deactivate", value: "DEACTIVATED", icon: <i className="fa-solid fa-toggle-off" /> },
  {
    label: "Send Email",
    value: "EMAIL",
    icon: <i className="fa-solid fa-envelope-circle-check" />,
  },
];

// Same first-address rule as the Engineers list.
const addressOf = (row) => row.user?.addresses?.[0];

const FreelanceCompanyMembersTable = ({ hideTitle = false, user = null }) => {
  // Embedded on a vendor's page the wrapper is already there, so it is not repeated.
  const Wrap = hideTitle ? Fragment : BaseWrapper;
  // On a vendor owner's page the route carries their user id, so the company comes from them.
  const { id: routeId } = useParams();
  const id = user?.company?.id ? String(user.company.id) : routeId;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });
  const [search, setSearch] = useState("");
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [isConfirmApproveOpen, setIsConfirmApproveOpen] = useState(false);
  const [isSendEmailToEngineerModalOpen, setIsSendEmailToEngineerModalOpen] = useState(false);
  const [isConfirmActivateOpen, setIsConfirmActivateOpen] = useState(false);
  const [isConfirmDeactivateOpen, setIsConfirmDeactivateOpen] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const memberFilters = useSelector((state) => state.adminFilters.freelanceCompanyMembers);

  const filtersPayload = useMemo(
    () => ({
      fullName: memberFilters.fullName || undefined,
      email: memberFilters.email || undefined,
      status: memberFilters.status || undefined,
    }),
    [memberFilters],
  );

  const queryParams = {
    page: pagination.page + 1,
    limit: pagination.pageSize,
    search,
  };

  const {
    data,
    isPending: isLoading,
    refetch,
  } = useQuery({
    queryKey: ["freelanceCompanyMembers", id, queryParams, filtersPayload],
    queryFn: () =>
      getAdminFreelanceCompanyMembers(id, {
        ...queryParams,
        ...filtersPayload,
        excludeOwner: true,
      }),
    keepPreviousData: true,
    enabled: !!id,
  });

  const { data: memberOptionsData } = useQuery({
    queryKey: ["freelanceMemberFilterOptions", id],
    queryFn: () => getAdminFreelanceCompanyMembers(id, { limit: 500, excludeOwner: true }),
    enabled: isFilterDrawerOpen && !!id,
    staleTime: 5 * 60 * 1000,
  });

  const nameOptions = useMemo(
    () =>
      [
        ...new Set(
          (memberOptionsData?.data?.data || [])
            .map((row) => `${row.user?.firstName || ""} ${row.user?.lastName || ""}`.trim())
            .filter(Boolean),
        ),
      ]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name })),
    [memberOptionsData],
  );

  const appliedFilters = useMemo(() => {
    const applied = [];
    if (memberFilters.fullName)
      applied.push({ prefix: "Name", value: memberFilters.fullName, key: "fullName" });
    if (memberFilters.email)
      applied.push({ prefix: "Email", value: memberFilters.email, key: "email" });
    if (memberFilters.status) {
      const statusLabelMap = {
        PENDING: "Pending",
        SUBMITTED: "Submitted",
        APPROVED: "Active",
        ACTIVATED: "Active",
        DEACTIVATED: "In-Active",
      };
      applied.push({
        prefix: "Status",
        value: statusLabelMap[memberFilters.status] || memberFilters.status,
        key: "status",
      });
    }
    return applied;
  }, [memberFilters]);

  const handleRemoveFilter = (key) => {
    dispatch(setAdminFilters({ module: "freelanceCompanyMembers", filterName: key, data: "" }));
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const { mutateAsync: approveEngineerFn, isPending: isApproving } = useMutation({
    mutationFn: (userId) => approveEngineerProfile(userId),
    onSuccess: () => {
      dispatch(showToast({ message: "Engineer approved successfully", type: "success" }));
      setIsConfirmApproveOpen(false);
      queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] });
    },
    onError: (error) => {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to approve engineer",
          type: "error",
        }),
      );
    },
  });

  const { mutateAsync: activateEngineerFn, isPending: isActivating } = useMutation({
    mutationFn: (userId) => activateEngineer(userId),
    onSuccess: () => {
      dispatch(showToast({ message: "Engineer activated successfully", type: "success" }));
      setIsConfirmActivateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] });
    },
    onError: () => {
      dispatch(showToast({ message: "Failed to activate engineer", type: "error" }));
    },
  });

  const { mutateAsync: deactivateEngineerFn, isPending: isDeactivating } = useMutation({
    mutationFn: (userId) => deactivateEngineer(userId),
    onSuccess: () => {
      dispatch(showToast({ message: "Engineer deactivated successfully", type: "success" }));
      setIsConfirmDeactivateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] });
    },
    onError: () => {
      dispatch(showToast({ message: "Failed to deactivate engineer", type: "error" }));
    },
  });

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setSearch(value);
      setPagination((p) => ({ ...p, page: 0 }));
    }, 500),
  ).current;

  const renderHeaderWithIcon = (title) => (
    <div className="flex items-center gap-2">
      <span className="text-[#525354] font-medium">{title}</span>
    </div>
  );

  const handleItemClick = (value, row) => {
    const engineer = row.user ? { ...row.user } : null;
    setSelectedEngineer(engineer);

    if (value === "VIEW") {
      navigate(`/user/details/profile/${row.user?.id}`);
    } else if (value === "APPROVE") {
      setIsConfirmApproveOpen(true);
    } else if (value === "APPROVED") {
      setIsConfirmActivateOpen(true);
    } else if (value === "DEACTIVATED") {
      setIsConfirmDeactivateOpen(true);
    } else if (value === "EMAIL") {
      setIsSendEmailToEngineerModalOpen(true);
    }
  };

  const columns = [
    {
      field: "userId",
      headerName: "ID",
      flex: 0.5,
      minWidth: 80,
      renderHeader: () => renderHeaderWithIcon("ID"),
      renderCell: (params) => (
        <p
          className="cursor-pointer text-primary"
          onClick={() => navigate(`/user/details/profile/${params.row.user?.id}`)}
        >
          {params.row.user?.id}
        </p>
      ),
    },
    {
      field: "firstName",
      headerName: "Full Name",
      flex: 1,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Full Name"),
      renderCell: (params) => (
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => navigate(`/user/details/profile/${params.row.user?.id}`)}
        >
          <Avatar
            src={params.row.user?.profilePicture || "/default-avatar.png"}
            alt={`${params.row.user?.firstName} ${params.row.user?.lastName}`}
            sx={{ width: 28, height: 28 }}
          />
          <span className="text-primary">
            {params.row.user?.firstName} {params.row.user?.lastName}
          </span>
          {params.row.isOwner && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-700">
              Owner
            </span>
          )}
        </div>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("Address"),
      renderCell: (params) => <p>{addressOf(params.row)?.addressLine1 || "-"}</p>,
    },
    {
      field: "city",
      headerName: "City",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("City"),
      renderCell: (params) => <p>{addressOf(params.row)?.city || "-"}</p>,
    },
    {
      field: "state",
      headerName: "State",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("State"),
      renderCell: (params) => <p>{addressOf(params.row)?.state || "-"}</p>,
    },
    {
      field: "zipCode",
      headerName: "Zip / Post Code",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("Zip / Post Code"),
      renderCell: (params) => <p>{addressOf(params.row)?.zipCode || "-"}</p>,
    },
    {
      field: "country",
      headerName: "Country",
      flex: 0.7,
      minWidth: 150,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("Country"),
      renderCell: (params) => <p>{addressOf(params.row)?.country || "-"}</p>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.2,
      minWidth: 220,
      renderHeader: () => renderHeaderWithIcon("Email"),
      renderCell: (params) => <p>{params.row.user?.email || "-"}</p>,
    },
    {
      field: "identity",
      headerName: "Identity",
      flex: 1.2,
      minWidth: 220,
      sortable: false,
      renderHeader: () => renderHeaderWithIcon("Identity"),
      // What the engineer uploaded under Verify your Identity.
      renderCell: (params) => <IdentityFiles documents={params.row.user?.identityDocuments} />,
    },
    {
      field: "phoneNumber",
      headerName: "Mobile Number",
      flex: 0.9,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Mobile Number"),
      renderCell: (params) => <p>{formatPhoneNumber(params.row.user?.phoneNumber) || "-"}</p>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Status"),
      renderCell: (params) => {
        const s = params.row.user?.status;
        if (s === "APPROVED")
          return (
            <Chip label="Active" color="success" size="small" className="w-24 justify-center" />
          );
        if (s === "DEACTIVATED")
          return (
            <Chip label="In-Active" color="error" size="small" className="w-24 justify-center" />
          );
        if (s === "SUBMITTED")
          return (
            <Chip
              label="Submitted"
              color="secondary"
              size="small"
              className="w-24 justify-center"
            />
          );
        if (s === "UNAPPROVE")
          return (
            <Chip
              label="Incomplete"
              size="small"
              className="w-24 justify-center"
              sx={{ backgroundColor: INCOMPLETE_CHIP_COLOR, color: "#fff" }}
            />
          );
        return (
          <Chip
            label="Pending"
            color="primary"
            size="small"
            variant="outlined"
            className="w-24 justify-center"
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Joined",
      flex: 0.8,
      minWidth: 150,
      renderHeader: () => renderHeaderWithIcon("Joined"),
      renderCell: (params) => (
        <p>{params.row.createdAt ? dayjs(params.row.createdAt).format("YYYY/MM/DD") : "-"}</p>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 90,
      renderHeader: () => renderHeaderWithIcon("Actions"),
      renderCell: (params) => {
        const userStatus = params.row.user?.status;
        const filtered = menuOptions.filter((item) => {
          if (userStatus === "PENDING") {
            return !["APPROVE", "DEACTIVATED", "APPROVED"].includes(item.value);
          }
          if (userStatus === "SUBMITTED") {
            return !["DEACTIVATED", "APPROVED"].includes(item.value);
          }
          if (userStatus === "APPROVED") {
            return !["APPROVED", "APPROVE", "EMAIL"].includes(item.value);
          }
          if (userStatus === "UNAPPROVE") {
            return !["APPROVED", "DEACTIVATED", "UNAPPROVE"].includes(item.value);
          }
          if (userStatus === "DEACTIVATED") {
            return !["DEACTIVATED", "APPROVE", "EMAIL"].includes(item.value);
          }
          return true;
        });
        return (
          <BaseDropdownV2
            menuOptions={filtered}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base" />}
            onItemClick={(item) => handleItemClick(item, params.row)}
          />
        );
      },
    },
  ];

  const handleCellClick = (params) => {
    if (params.field === "userId" || params.field === "firstName") {
      navigate(`/user/details/profile/${params.row.user?.id}`);
    }
  };

  const companyName = data?.data?.companyName;
  const members = data?.data?.data || [];
  const totalCount = data?.data?.totalCount || 0;

  return (
    <>
      {!hideTitle && (
        <div className="border-b border-zinc-300 bg-white">
          <BaseWrapper>
            <div className="grid grid-cols-7 gap-y-3 py-[7px] items-center">
              <div className="md:col-span-3 col-span-7 flex items-center gap-3">
                <h3 className="md:text-left text-center">
                  {companyName ? `${companyName}'s Members` : "Vendor Members"}
                </h3>
              </div>
            </div>
          </BaseWrapper>
        </div>
      )}
      <Wrap>
        <div className="bg-white rounded-lg px-5 py-3.5 shadow-md mt-4 sticky top-10 z-10">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 w-full">
            <div
              className="flex items-center border-r-[1px] border-[#8C8C8C] pr-5 cursor-pointer"
              onClick={() => setIsFilterDrawerOpen(true)}
            >
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary">
                <i className="fa-light fa-filter text-white text-sm"></i>
              </span>
              <p className="text-primary ml-2 ">Add Filter</p>
            </div>
            <div className="flex-1 min-w-0 pl-3 overflow-x-auto whitespace-nowrap py-1 scrollbar">
              <div className="flex gap-2">
                {appliedFilters.map((f) => (
                  <div
                    key={f.key}
                    className="flex-shrink-0 flex items-center bg-[#EAF1FF] text-sm font-medium rounded-md px-3 py-1"
                  >
                    <span className="text-primary">{f.prefix}:</span>&nbsp;
                    <p className="text-primary text-sm mx-1">{f.value}</p>
                    <button
                      onClick={() => handleRemoveFilter(f.key)}
                      className="ml-2 text-primary hover:text-red-500"
                      aria-label={`Remove ${f.prefix} ${f.value}`}
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full sm:w-[28%]">
              <BaseTextField
                placeholder="Search by name, email or ID"
                onChange={(e) => debouncedSearchRef(e.target.value)}
                startAdornment={<i className="fa-solid fa-magnifying-glass text-lg" />}
              />
            </div>
          </div>
        </div>

        <BaseDataTableV2
          className="max-h-[calc(100vh-320px)] overflow-auto mt-4"
          rowHeight={38}
          columns={columns}
          data={members}
          isLoading={isLoading}
          onCellClick={handleCellClick}
          hideFooter={false}
          pagination
          rowCount={totalCount}
          pageSizeOptions={[10, 20, 50]}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          paginationMode="server"
        />

        {isFilterDrawerOpen && (
          <AdminFilters
            open={isFilterDrawerOpen}
            onClose={() => setIsFilterDrawerOpen(false)}
            fetchData={refetch}
            isLoading={isLoading}
            setFiltersChanged={() => {}}
            entity="freelanceCompanyMembers"
            nameOptions={nameOptions}
          />
        )}

        <BaseConfirmModal
          open={isConfirmApproveOpen}
          onClose={() => setIsConfirmApproveOpen(false)}
          onConfirm={() => approveEngineerFn(String(selectedEngineer?.id))}
          isLoading={isApproving}
          title="Are you sure you want to approve this engineer?"
          description={
            <span>
              A confirmation email will be sent to{" "}
              <span className="underline font-medium">{selectedEngineer?.email}</span>.
            </span>
          }
          confirmText="Confirm"
          confirmButtonColor="primary"
        />

        {isSendEmailToEngineerModalOpen && (
          <SendEmailToEngineerModal
            setIsSendEmailToEngineerModalOpen={setIsSendEmailToEngineerModalOpen}
            isSendEmailToEngineerModalOpen={isSendEmailToEngineerModalOpen}
            onStatusChange={() =>
              queryClient.invalidateQueries({ queryKey: ["freelanceCompanyMembers"] })
            }
            selectedEngineer={selectedEngineer}
          />
        )}

        <BaseConfirmModal
          open={isConfirmActivateOpen}
          onClose={() => setIsConfirmActivateOpen(false)}
          onConfirm={() => activateEngineerFn(String(selectedEngineer?.id))}
          isLoading={isActivating}
          title="Are you sure you want to activate this engineer?"
          description="Once activated, the engineer will regain access and their status will be set to Active."
          confirmText="Activate"
          confirmButtonColor="primary"
        />

        <BaseConfirmModal
          open={isConfirmDeactivateOpen}
          onClose={() => setIsConfirmDeactivateOpen(false)}
          onConfirm={() => deactivateEngineerFn(String(selectedEngineer?.id))}
          isLoading={isDeactivating}
          title="Are you sure you want to deactivate this engineer?"
          description="Once deactivated, the engineer will lose access and their status will be set to Inactive."
          confirmText="Deactivate"
          confirmButtonColor="error"
        />
      </Wrap>
    </>
  );
};

export default FreelanceCompanyMembersTable;
